import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';

import { CLIENT_CONTEXT_TOKEN_DCS } from '@/api/dcs-api/tokens';

import { extractProblemDetailsBody } from './problem-details.util';

/**
 * Preserves RFC 9457 ProblemDetails bodies for DCS requests (ADR-013 D3).
 *
 * The global `ErrorInterceptor` (`@trueform/ui/core`) rewrites every
 * `HttpErrorResponse` into a legacy DCS-style `ErrorPayload`, synthesizing
 * `{ code: 'HTTP_ERROR', message, message_key }` whenever the body is not the
 * legacy DCS `{ error: { code, … } }` envelope — so DCS 0.3.0's TOP-LEVEL ProblemDetails
 * body (`title`, `detail`, `code`, `correlation_id`, `error_id` on 5xx)
 * is destroyed before any service subscriber sees it, degrading every DCS 4xx
 * to `HTTP_ERROR` and killing the catalog kit's `CONCURRENCY_CONFLICT` reconcile
 * flow and `RESOURCE_REFERENCED` handling.
 *
 * Errors propagate innermost-first, so this interceptor MUST be registered
 * AFTER `ErrorInterceptor` in `HTTP_INTERCEPTORS` (in `app.config.ts`,
 * alongside `PcsProblemDetailsInterceptor`). It rethrows the ProblemDetails BODY
 * as a plain object: the outer `ErrorInterceptor`'s `instanceof HttpErrorResponse`
 * check then fails and passes it through untouched (no toast), while catalog-fetcher
 * reads `err.code` directly (`CONCURRENCY_CONFLICT`, `RESOURCE_REFERENCED`).
 *
 * The defusing scope is deliberately narrow:
 * - only requests marked as DCS (`CLIENT_CONTEXT_TOKEN_DCS`);
 * - only non-401 failures — 401 keeps the global logout/redirect flow;
 * - only ProblemDetails-shaped bodies (a record with a non-blank string `title`).
 */
@Injectable()
export class DcsProblemDetailsInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.context.has(CLIENT_CONTEXT_TOKEN_DCS)) {
      return next.handle(req);
    }
    return next
      .handle(req)
      .pipe(catchError((err: unknown) => throwError(() => extractProblemDetailsBody(err) ?? err)));
  }
}
